import {
  EJobTypeWithUnemployed,
  EmploymentInput,
  EmploymentInputSchema,
  FORM_ID,
  InputText,
  PhoneExtInput,
  PhoneInput,
  ScrollableAlert,
  SelectInput,
  translateFieldError,
  useTranslatedOptionEnum,
} from '@applyonline/shared'
import { calculateTotalMonths, helpTip, useFormAutoSave } from '@applyonline/shared/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  EJobType,
  EOtherIncomeType,
  getEJobTypeSelectableList,
  getEOtherIncomeTypeSelectableList,
} from '@if/core/types'
import { range } from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { APPLY_MECH_WARRANTY_CONSTANTS } from '../../../types/constants'

interface Props {
  applicantData: EmploymentInput
  onApplicantChanged: (data: EmploymentInput) => void
  nextStepAfter: (currentStep: string) => void
  currentStep: string
}

const EmploymentForm = ({ onApplicantChanged, nextStepAfter, currentStep, applicantData }: Props) => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const [displayMinIncomeMessage, setDisplayMinIncomeMessage] = useState(false)
  const [displayMaxIncomeMessage, setDisplayMaxIncomeMessage] = useState(false)
  const {
    handleSubmit,
    register,
    formState: { errors },
    setValue,
    watch,
    control,
    subscribe,
  } = useForm<EmploymentInput>({
    mode: 'onBlur',
    resolver: yupResolver(EmploymentInputSchema),
    defaultValues: applicantData,
  })

  useFormAutoSave(subscribe, onApplicantChanged)

  const watchOtherIncomeTypes = watch('otherIncomes.0.typeId')
  const jobType = watch('currentJobs.0.jobType')
  const currentJobSalary = watch('currentJobs.0.annualSalary')
  const otherIncomeAnnualSalary = watch('otherIncomes.0.annualAmount')
  const watchYears = watch('currentJobs.0.years')

  const onSubmit: SubmitHandler<EmploymentInput> = (payload) => {
    if (
      Number(currentJobSalary ?? 0) + Number(otherIncomeAnnualSalary ?? 0) >=
        APPLY_MECH_WARRANTY_CONSTANTS.MinIncomes &&
      Number(currentJobSalary ?? 0) <= APPLY_MECH_WARRANTY_CONSTANTS.MaxIncomes &&
      Number(otherIncomeAnnualSalary ?? 0) <= APPLY_MECH_WARRANTY_CONSTANTS.MaxIncomes
    ) {
      onApplicantChanged(payload)
      nextStepAfter(currentStep)
    }
    if (
      Number(currentJobSalary ?? 0) > APPLY_MECH_WARRANTY_CONSTANTS.MaxIncomes ||
      Number(otherIncomeAnnualSalary ?? 0) > APPLY_MECH_WARRANTY_CONSTANTS.MaxIncomes
    ) {
      setDisplayMaxIncomeMessage(true)
    } else {
      setDisplayMinIncomeMessage(true)
    }
  }

  useEffect(() => {
    const years = +(watchYears ?? 0)
    const total = calculateTotalMonths(years)

    setValue('currentJobs.0.months', total)
  }, [setValue, watchYears])

  useEffect(() => {
    helpTip()
  }, [applicantData])

  useEffect(() => {
    if ((Number(jobType) as EJobType | EJobTypeWithUnemployed) === EJobTypeWithUnemployed.Unemployed) {
      setValue('currentJobs.0.annualSalary', null, { shouldValidate: true })
    }
  }, [jobType, setValue])
  useEffect(() => {
    setValue('otherIncomes.0.jobType', jobType)
  }, [jobType, setValue])

  useEffect(() => {
    if (!watchOtherIncomeTypes) {
      setValue('otherIncomes.0.annualAmount', null)
    }
  }, [setValue, watchOtherIncomeTypes])

  const jobTypeOptions = useMemo(
    () => [
      ...getEJobTypeSelectableList().map((item) => (
        <option key={item} value={item}>
          {t(`EJobType.${item}`, { ns: 'enum' })}
        </option>
      )),
      <option key={EJobTypeWithUnemployed.Unemployed} value={EJobTypeWithUnemployed.Unemployed}>
        {t('income.unemployed')}
      </option>,
    ],
    [t],
  )
  const otherIncomeTypesOptions = useTranslatedOptionEnum(
    'EOtherIncomeType',
    getEOtherIncomeTypeSelectableList(),
    'enum',
  )

  return (
    <section className="step-content">
      <h3 className="form-question">{t('income.howMuch')}</h3>

      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID} noValidate>
        <ScrollableAlert
          showAlert={displayMinIncomeMessage || displayMaxIncomeMessage}
          type="warn"
          message={displayMinIncomeMessage ? t('income.minAnnualSalary') : t('income.maxAnnualSalary')}
        />
        <SelectInput
          id="employment_category"
          label="income.category"
          error={errors.currentJobs?.[0]?.jobType}
          hasEmptyValue
          disableEmptyValue
          defaultValue=""
          options={jobTypeOptions}
          {...register('currentJobs.0.jobType')}
        />

        {(Number(jobType) as EJobType | EJobTypeWithUnemployed) !== EJobTypeWithUnemployed.Unemployed && (
          <>
            <div className={`control-group  ${errors.currentJobs?.[0]?.annualSalary && 'error'}`}>
              <label htmlFor="gross_anual">
                {t('income.beforeDeductions')}
                <span className="help-tip">
                  <p>{t('income.grossAnnualInfo')}</p>
                </span>
              </label>
              <div className={`number-wrap ${lang === 'en' ? 'startwith' : 'endwith'}`}>
                <InputText
                  type="number"
                  title={t('income.grossAnnualInfo')}
                  id="annualSalary"
                  error={errors.currentJobs?.[0]?.annualSalary}
                  {...register('currentJobs.0.annualSalary')}
                />
              </div>
            </div>
            <div className={`control-group ${errors.currentJobs?.[0]?.jobTitle && 'error'}`}>
              <label htmlFor="job_title">{t('income.jobTitle')}</label>
              <input type="text" id="jobTitle" {...register('currentJobs.0.jobTitle')} maxLength={50} />
            </div>

            <div className="control-group-wrap" style={{ alignItems: 'start' }}>
              <div className={`control-group mid ${errors.currentJobs?.[0]?.employerName && 'error'}`}>
                <label htmlFor="employer_name">{t('income.employerName')}</label>
                <input type="text" id="employerName" {...register('currentJobs.0.employerName')} maxLength={100} />
              </div>

              <PhoneInput
                className="sml"
                control={control}
                name="currentJobs.0.employerPhone"
                label={t('income.phoneNumber')}
              />
              <PhoneExtInput
                className="sml"
                control={control}
                name="currentJobs.0.employerPhoneExt"
                label={t('income.extension')}
              />
            </div>

            <div className="control-group mid">
              <label htmlFor="employment_years">{t('income.employmentLenght')}</label>
              <div className={`dropdown-wrap ${errors.currentJobs?.[0]?.months ? 'error' : ''}`}>
                <select id="year" {...register('currentJobs.0.years')}>
                  <option value={0}>{t('common.years')}</option>
                  <option value={-1}>{t('common.lessThanOneYear')}</option>
                  {range(1, 15).map((item: number) => (
                    <option value={item} key={item}>
                      {t('common.yearsLabel', { count: item })}
                    </option>
                  ))}
                  <option value={15}>
                    {t('common.moreThan')} {t('common.yearsLabel', { count: 15 })}
                  </option>
                </select>
              </div>
            </div>
          </>
        )}

        <div className="control-group">
          <h1 className="h3">
            {t('income.otherIncome')}
            <span className="help-tip">
              <p>{t('income.otherIncomeInfo')}</p>
            </span>
          </h1>
        </div>
        <div className="control-group mid">
          <label htmlFor="otherIncomeType">{t('income.otherIncome')}</label>
          <div className={`dropdown-wrap ${errors.otherIncomes?.[0]?.typeId && 'error'}`}>
            <select id="otherIncomeType" {...register('otherIncomes.0.typeId')}>
              <option aria-label={t('common.none')}> </option>
              {otherIncomeTypesOptions}
            </select>
          </div>
        </div>

        <div className={`control-group mid ${errors.otherIncomes?.[0]?.annualAmount && 'error'}`}>
          <label htmlFor="other_source">{t('income.annualIncome')}</label>
          <div className={`number-wrap ${lang === 'en' ? 'startwith' : 'endwith'}`}>
            <input
              type="number"
              id="annualAmount"
              disabled={!watchOtherIncomeTypes}
              {...register('otherIncomes.0.annualAmount')}
            />
            {errors.otherIncomes?.[0]?.annualAmount && (
              <p className="error-message" style={{ position: 'absolute' }}>
                {translateFieldError(t, errors.otherIncomes?.[0]?.annualAmount, { context: 'annualAmount' })}
              </p>
            )}
          </div>
        </div>

        {(Number(watchOtherIncomeTypes) as EOtherIncomeType) === EOtherIncomeType.Other && (
          <div className={`control-group ${errors.otherIncomes?.[0]?.description && 'error'}`}>
            <label htmlFor="other_source">{t('income.otherIncomeDescription')}</label>
            <input type="text" id="description" {...register('otherIncomes.0.description')} maxLength={50} />
          </div>
        )}
      </form>
    </section>
  )
}

export default EmploymentForm
